(ns story-builder.event.util
  (:require
   [alandipert.storage-atom :as storage-atom]
   [camel-snake-kebab.core :refer [->camelCase
                                   ->kebab-case]]
   [camel-snake-kebab.extras :refer [transform-keys]]
   [cljs-time.core :as t]
   [cljs.pprint :refer [pprint]]
   [clojure.string :as str]
   [goog.object :as gobj]
   [graphql-query.core]
   [re-frame.core :as rf]
   [re-frame.utils :as rf-utils]
   [reagent.core :as r]
   [story-builder.data-library.event :as-alias data-library.event]
   [story-builder.debug :as debug]
   [story-builder.define :as define]
   [story-builder.spec.story :as spec.story]
   [story-builder.storage :refer [persist-db
                                  reg-co-fx!]]
   [story-builder.story-editor.animation-sidebar.util :as animation-sidebar.util]
   [story-builder.story-editor.controller.sub :as controller.sub]
   [story-builder.translation :refer [tr]]
   [story-builder.util.core :as util]
   [story-builder.util.url :as util.url]
   [vimsical.re-frame.cofx.inject :as sub-inject]))

(defn action-description-by-layer-type [action layer-type]
  (let [displayed-layer-type (case layer-type
                               "title"      "text"
                               "subheading" "text"
                               "author"     "text"
                               "date"       "text"
                               layer-type)]
    (case [action displayed-layer-type]
      [:add "img"]         (tr [:add-image "Add image"])
      [:add "video"]       (tr [:add-video "Add video"])
      [:add "shape"]       (tr [:add-shape "Add shape"])
      [:add "text"]        (tr [:add-text "Add text"])
      [:add "captions"]    (tr [:add-captions "Add captions"])
      [:add "embed"]       (tr [:add-embed "Add embed"])
      [:remove "img"]      (tr [:remove-image "Remove image"])
      [:remove "video"]    (tr [:remove-video "Remove video"])
      [:remove "shape"]    (tr [:remove-shape "Remove shape"])
      [:remove "text"]     (tr [:remove-text "Remove text"])
      [:remove "captions"] (tr [:remove-captions "Remove captions"])
      [:remove "embed"]    (tr [:remove-embed "Remove embed"])
      [:move "img"]        (tr [:move-image "Move image"])
      [:move "video"]      (tr [:move-video "Move video"])
      [:move "shape"]      (tr [:move-shape "Move shape"])
      [:move "text"]       (tr [:move-text "Move text"])
      [:move "captions"]   (tr [:move-captions "Move captions"])
      [:move "embed"]      (tr [:move-embed "Move embed"])
      [:rotate "img"]      (tr [:rotate-image "Rotate image"])
      [:rotate "video"]    (tr [:rotate-video "Rotate video"])
      [:rotate "shape"]    (tr [:rotate-shape "Rotate shape"])
      [:rotate "text"]     (tr [:rotate-text "Rotate text"])
      [:rotate "captions"] (tr [:rotate-captions "Rotate captions"])
      [:rotate "embed"]    (tr [:rotate-embed "Rotate embed"])
      [:resize "img"]      (tr [:resize-image "Resize image"])
      [:resize "video"]    (tr [:resize-video "Resize video"])
      [:resize "shape"]    (tr [:resize-shape "Resize shape"])
      [:resize "text"]     (tr [:resize-text "Resize text"])
      [:resize "captions"] (tr [:resize-captions "Resize captions"])
      [:resize "embed"]    (tr [:resize-embed "Resize embed"])
      ;; Default to use "layer" if specific combination is not in the above list
      (case action
        :add    (tr [:add-layer "Add layer"])
        :remove (tr [:remove-layer "Remove layer"])
        :move   (tr [:move-layer "Move layer"])
        :rotate (tr [:rotate-layer "Rotate layer"])
        :resize (tr [:resize-layer "Resize layer"])
        (tr [:update-layer "Update layer"])))))

(comment
  (action-description-by-layer-type :add "title") ; => "Add text"
  (action-description-by-layer-type :add "bg") ; => "Add layer"
  (action-description-by-layer-type :remove "title") ; => "Remove text"
  (action-description-by-layer-type :resize "img") ; => "Resize image"
  )

(defn undo-explanation
  [db [k & args]]
  (case k
    :scenes/update-currents-layer
    (let [[[layer-id & params] fn & kvs] args]
      (if-not (seq params)
        (tr [:update-layer "Update layer"])
        (tr [:changed-layer "Changed layer %1"] [(-> params first name (str/replace "-" " "))])))
    :scenes/layer-update
    (let [[layer-id f first-param] args]
      (if (keyword? first-param)
        (tr [:changed-layer "Changed layer %1" [(-> first-param name (str/replace "-" " "))]])
        (tr [:update-layer "Update layer"])))
    :scenes/animation!
    (let [[_ animation] args]
      (tr [:animate-layer-with "Animate layer with %1"] [animation]))
    :scenes/apply-styles
    (tr [:apply-styles "Apply styles"])
    :scenes/update-layer
    (let [[params] args]
      (tr [:update-layer-x "Update layer %1: %2"]
          [(name (:update-key params)) (util/trunc (str (:update-value params)) 25)]))
    :scenes/move-z-index
    (let [[[from to]] args]
      (tr [:moved-layer-from-to "Move layer %1 to idx %2"]
          [from to]))
    :scenes/apply-template!
    (let [[_ new?] args]
      (if new?
        (tr [:add-scene "Add scene"])
        (tr [:apply-template "Apply template"])))
    :scenes/add-or-replace-attachment
    (let [[_ _] args]
      (tr [:add-attachment "Add attachment"]))
    :scenes/remove-attachment
    (let [[_ _] args]
      (tr [:remove-attachment "Remove attachment"]))
    :scenes/shift
    (let [[idx direction] args]
      (tr [:move-scene-to-the "Move scene %1  to the %2"]
          [idx (name direction)]))
    :scenes/toggle-correct-answer
    (tr [:toggle-quiz-answer "Toggle quiz answer"])
    :scenes/select-font-family!
    (let [[font] args]
      (tr [:set-font-to "Set font to %1"]
          [(:file-name font)]))
    :scenes/set-in-layer-style
    (let [[prop v _ _] args]
      (case prop
        :padding
        (tr [:set-padding-to "Set layer padding to %1"] [v])
        :opacity
        (tr [:set-opacity-to "Set layer opacity to %1"] [(.toFixed v 2)])
        :font-size
        (tr [:set-font-size-to "Set font size to %1"] [v])
        :font-style
        (tr [:set-font-style-to "Set font style to %1"] [v])
        :font-weight
        (tr [:set-font-weight-to "Set font weight to %1"] [v])
        :line-height
        (tr [:set-line-height-to "Set line height to %1"] [v])
        :letter-spacing
        (tr [:set-letter-spacing-to "Set letter spacing to %1"] [v])
        :text-align
        (tr [:set-text-alignment "Set text alignment %1"] [v])
        :text-transform
        (if (str/blank? v)
          (tr [:remove-text-transform "Remove text transform"])
          (tr [:set-text-transform-to "Set text transform to %1"] [v]))
        :text-decoration-line
        (if (str/blank? v)
          (tr [:remove-text-decoration-line "Remove text decoration line"])
          (tr [:set-text-decoration-line-to "Set text decoration line to %1"] [v]))
        :text-decoration-style
        (if (str/blank? v)
          (tr [:remove-text-decoration-style "Remove text decoration style"])
          (tr [:set-text-decoration-style-to "Set text decoration style to %1"] [v]))
        (pr-str k args)))
    :scene/delete-amp-interactive-result-category!
    (tr [:remove-results-category "Remove results category"])
    :scenes/update-amp-interactive-results-category!
    (tr [:update-results-category "Update results category"])
    :scenes/update!
    (let [[_ _ & ks] args
          params   (first ks)
          value    (second ks)]
      (case params
        [:amp :interactive :theme]
        (tr [:update-interactive-component-theme-to "Update interactive component theme to %1"]
            [value])
        [:amp :interactive :accent-color]
        (tr [:update-interactive-component-accent-color-to "Update interactive component accent colour to %1"]
            [value])
        [:amp :interactive :chip-style]
        (tr [:update-interactive-component-chip-style-to "Update interactive component chip style to %1"]
            [value])
        [:amp :interactive :prompt :background-color]
        (tr [:update-interactive-prompt-background-color-to "Update interactive prompt background colour to %1"]
            [value])
        [:amp :interactive :prompt :background :type]
        (tr [:update-interactive-prompt-background-type-to "Update interactive prompt background type to %1"]
            [value])
        (tr [:update-x-to-y "Update %1 to %2"]
            [(str/join " " (map name params)) value])))
    (util/trunc (pr-str k args) 30)))

(def history-limit 50)

;; Note: Always make sure that none of the paths in that list is prefix of another.
(def ^:private restorable-data-paths
  [[:persistent :data-library]
   [:persistent :story :story-config]
   [:persistent :story :editor-config :ruler]
   [:persistent :shape.drawing/paths]
   [:story/last-edit]])

(defn- gather-restorable-data
  "Get the piece of data which we want to save in the undo system."
  [db]
  (into []
        (map (fn [path]
               [path (get-in db path)]))
        restorable-data-paths))

(defn assoc-restorable-data
  "Returns an updated db containing the data saved by the function `gather-restorable-data`."
  [db saved-data]
  (reduce (fn [db [path data]]
            (assoc-in db path data))
          db
          saved-data))

(defn- transform-history-entries
  "Transforms the last entries of the undo/redo history."
  [previous-entry new-entry]
  (cond
    ;; Keep the previous entry if its group-id is different than the new entry.
    (or (nil? (:group-id new-entry))
        (not= (:group-id previous-entry)
              (:group-id new-entry)))
    [previous-entry new-entry]

    ;; Keep the previous entry if it is older than the debounce time.
    (and (:debounce-time new-entry)
         (>= (- (:stamp new-entry) (:stamp previous-entry))
             (:debounce-time new-entry)))
    [previous-entry new-entry]

    ;; The default for same group-id entries is to overwrite the previous entry.
    :else
    [new-entry]))

(defn create-undoable-interceptor
  [{:keys [history-path]
    :or   {history-path [:story/history]}
    :as   history-action}]
  (rf/->interceptor
   :id :undo-redo-interceptor
   :after (fn [context]
            (let [{:keys [coeffects effects]} context
                  now            (js/Date.)
                  before-db      (:db coeffects)
                  after-db       (-> (:db effects before-db)
                                     (assoc :story/last-edit now))
                  before-config  (get-in before-db [:persistent :story :story-config])
                  after-config   (get-in after-db [:persistent :story :story-config])
                  user           (-> before-db :local-storage :user)
                  media-library? (-> before-db :media-library :media-library/active?)
                  event          (:event coeffects)
                  history-path   (if (fn? history-path)
                                   (history-path coeffects)
                                   history-path)
                  history-action (cond
                                   (fn? history-action)   (let [coeffects (:coeffects context)
                                                                event     (:event coeffects)]
                                                            (history-action coeffects event))
                                   (some? history-action) history-action
                                   :else                  (:history/action effects))
                  edit?          (or (not (identical? before-config after-config))
                                     (not (identical? (get-in before-db [:persistent :story :editor-config :ruler])
                                                      (get-in after-db [:persistent :story :editor-config :ruler])))
                                     ;; {:force? true} will force an edit if the restorable data has changed since the last history entry
                                     (and (:force? history-action)
                                          (let [history              (get-in after-db history-path)
                                                last-restorable-data (:after (last history))
                                                new-restorable-data  (gather-restorable-data after-db)]
                                            (not= last-restorable-data new-restorable-data))))
                  dataset-edit?  (if (= (:route after-db) :scenes)
                                   (not (identical? (get-in before-db [:persistent :story :data-source])
                                                    (get-in after-db [:persistent :story :data-source])))
                                   (not (identical? (get-in before-db [:persistent :data-library :data-library/selected])
                                                    (get-in after-db [:persistent :data-library :data-library/selected]))))]
              (if (= (-> after-db :local-storage :user :role) "guest")
                context
                (update context :effects
                        (fn [effects]
                          (cond-> (dissoc effects :history/action)
                            dataset-edit?
                            (assoc :fx [[:dispatch-debounce [::save-dataset [::data-library.event/autosave {}]
                                                             2000]]])
                            edit?
                            (assoc :db (-> after-db
                                           (update-in history-path
                                                      (fn [history]
                                                        (let [;; If the history is empty, it should start with an initial state to which we can rollback to in the UI.
                                                              history (if (empty? history)
                                                                        [{:active?     true
                                                                          :after       (gather-restorable-data before-db)
                                                                          :description (tr [:initial-state "Initial state"])
                                                                          :stamp       now}]
                                                                        history)

                                                              ;; Find the active entry.
                                                              active-entry-idx (util/idx-by history :active? true)

                                                              ;; Mark all entries as non-active.
                                                              history (mapv #(assoc % :active? false) history)

                                                              active-entry (get history active-entry-idx)

                                                              ;; Get rid of the entries starting from the active entry.
                                                              history (subvec history 0 active-entry-idx)

                                                              ;; The new entry.
                                                              new-entry {:active?       true
                                                                         :after         (gather-restorable-data after-db)
                                                                         :group-id      (:group-id history-action)
                                                                         :description   (or (:description history-action)
                                                                                            (undo-explanation before-db event))
                                                                         :stamp         now
                                                                         :debounce-time (:debounce-time history-action)
                                                                         :event         event}

                                                              ;; Append the transform of active-entry and new-entry.
                                                              history (into history (transform-history-entries active-entry new-entry))

                                                              ;; If the history is too long, drop its oldest entries.
                                                              history (if (> (count history) history-limit)
                                                                        (subvec history (- (count history) history-limit))
                                                                        history)]
                                                          history)))))

                            true
                            (dissoc :history/action)

                            (and define/autosave?
                                 (not media-library?)
                                 (not (:iba user)))
                            (assoc :dispatch-debounce [::save! [:stories/save! {}] 5000])))))))))

(set! graphql-query.core/*kw->gql-name*
      (fn [kw]
        (if (or (contains? #{:String} kw)
                (str/includes? (name kw) "Input"))
          (name kw)
          (name (->camelCase kw)))))

(defn relay-reducer [res [k v]]
  (let [context (volatile! {})
        k       (if (re-matches spec.story/uuid-regex (name k))
                  k
                  (->kebab-case k))
        v       (if (contains? v :edges)
                  (let [total     (:totalCount v)
                        page-info (:pageInfo v)]
                    (when total
                      (vswap! context assoc-in [:totals k]
                              (transform-keys ->kebab-case total)))
                    (when page-info
                      (vswap! context assoc-in [:page-infos k]
                              (transform-keys ->kebab-case page-info)))
                    (mapv (fn [{:keys [node]}]
                            (if (string? node)
                              node
                              (reduce relay-reducer {} node)))
                          (:edges v)))
                  v)]
    (merge @context
           (assoc res k
                  (cond
                    (map? v) (reduce relay-reducer {} v)
                    (vector? v) (mapv #(if (map? %) (reduce relay-reducer {} %) %) v)
                    :else v)))))

(when define/frontend?
  (def persist-db-interceptor
    (persist-db :nws-studio :persistent)))

(def undoable-interceptor
  (create-undoable-interceptor nil))

(def path-persistent-interceptor
  (rf/path [:persistent]))

(def scenes-path-interceptor
  (rf/path [:persistent :story :story-config :scenes]))

(def local-storage-path-interceptor
  (rf/path [:local-storage]))

(when define/frontend?
  (def local-storage-persist-interceptor
    (persist-db :nws-studio-local-storage :local-storage storage-atom/local-storage)))

(def local-storage-persistent-interceptors
  [local-storage-persist-interceptor
   local-storage-path-interceptor])

(when define/frontend?
  (reg-co-fx! :nws-studio {:fx :session-storage :cofx :session-storage})
  (reg-co-fx! :nws-studio-local-storage {:fx :local-storage :cofx :local-storage} storage-atom/local-storage))

(def ^:private debounce-id->timeout-id (atom {}))

(defn dispatch-debounce
  ([event]
   (dispatch-debounce nil event))
  ([opts event]
   (let [{:keys [id interval]
          :or {id       (first event)
               interval 500}} opts]
     ;; Prevent existing setTimeout from triggering.
     (when-some [timeout-id (@debounce-id->timeout-id id)]
       (js/clearTimeout timeout-id))

     (if (zero? interval)
       (rf/dispatch event) ;; Immediate dispatch
       (let [;; Schedule the dispatch of the event
             timeout-id (js/setTimeout (fn []
                                         (swap! debounce-id->timeout-id dissoc id)
                                         (rf/dispatch event))
                                       interval)]
         (swap! debounce-id->timeout-id assoc id timeout-id))))))

(defn find-layer-location
  "Returns the layer's location [scene-index layer-index], or nil when not found."
  [scenes layer-id]
  (-> (for [[scene-index scene] (util/seq-indexed scenes)
            [layer-index layer] (util/seq-indexed (:layers scene))
            :when (= (:id layer) layer-id)]
        [scene-index layer-index])
      first))

(comment
  ;; => [1 0]
  (find-layer-location [{:layers [{:id 1}
                                  {:id 2}]}
                        {:layers [{:id 3}
                                  {:id 4}]}]
                       3)

  ;; => nil
  (find-layer-location [{:layers [{:id 1}
                                  {:id 2}]}
                        {:layers [{:id 3}
                                  {:id 4}]}]
                       5))

(defn layer-from-location [scenes [scene-index layer-index]]
  (get-in scenes [scene-index :layers layer-index]))

(defn get-layer [scenes layer-id]
  (some->> (find-layer-location scenes layer-id)
           (layer-from-location scenes)))

(defn update-layer [scenes layer-id f & args]
  (if-some [[scene-index layer-index] (find-layer-location scenes layer-id)]
    (update-in scenes [scene-index :layers]
               (fn [layers]
                 (-> (apply update layers layer-index f args)
                     spec.story/sort-layers)))
    scenes))

(defn layer-id->scene-id [scenes layer-id]
  (let [[scene-index _] (find-layer-location scenes layer-id)]
    (get-in scenes [scene-index :id])))

(defn find-scene-index [scenes scene-id]
  (util/idx-by scenes :id scene-id))

(defn get-scene [scenes scene-id]
  (some->> (find-scene-index scenes scene-id)
           (get scenes)))

(defn update-scene [scenes scene-id f & args]
  (if-some [scene-index (find-scene-index scenes scene-id)]
    (update scenes scene-index
            (fn [scene]
              (-> (apply f scene args)
                  (update :layers spec.story/sort-layers))))
    scenes))

(rf/reg-event-fx
 :query-string!
 (fn [_ [_ data]]
   (let [merged (util/remove-by str/blank? (merge (util.url/qs->clj) data))]
     (.replaceState js/history
                    js/undefined
                    js/undefined
                    (str js/window.location.pathname
                         (when-not (empty? merged)
                           (str "?" (util.url/clj->qs merged))))))
   {}))

(rf/reg-event-fx
 :reset-query-string!
 (fn [_ [_ data]]
   (.replaceState js/history
                  js/undefined
                  js/undefined
                  (str js/window.location.pathname
                       (when-not (empty? data)
                         (str "?" (util.url/clj->qs data)))))
   {}))

(rf/reg-cofx
 :today-time
 (fn [coeffects _]
   (assoc coeffects :today-time (t/today-at 0 0))))

(rf/reg-cofx
 :months
 (fn [coeffects _]
   (assoc coeffects :months (t/months 1))))

(rf/reg-cofx
 :date-range
 (fn [coeffects _]
   (let [{:keys [ui/start ui/end]} (:db coeffects)]
     (assoc coeffects :start start :end end))))

(rf/reg-cofx
 :now
 (fn [coeffects _]
   (assoc coeffects :now (t/now))))

(rf/reg-cofx
 :random-id
 (fn [coeffects coeffect-key]
   (assoc coeffects (if (keyword? coeffect-key)
                      coeffect-key
                      :random-id) (str (random-uuid)))))

(rf/reg-event-db
 :set-page-zoom
 (fn [db [_ zoom]]
   (assoc db :page-zoom zoom)))

(rf/reg-event-db
 :assoc-in
 (fn [db [_ path v]]
   (assoc-in db path v)))

(rf/reg-event-db
 :dissoc-in
 (fn [db [_ path]]
   (rf-utils/dissoc-in db path)))

(rf/reg-event-db
 :update-in
 (fn [db [_ path fun & kvs]]
   (apply update-in db path fun kvs)))

(rf/reg-event-db
 :utils/unit-data
 (fn [db [_ {:keys [outer-scene-rect
                    scene-rect
                    unit-data]}]]
   (let [size         (-> scene-rect
                          (select-keys [:width :height])
                          vals)
         reference-em (* 15 (/ (apply min size) 375))]
     (-> db
         (assoc :ui/unit-data unit-data)
         (assoc-in [:ui :ui/outer-scene-rect] outer-scene-rect)
         (assoc-in [:utils :scene-rect] scene-rect)
         (assoc-in [:utils :reference-em] reference-em)))))

(rf/reg-event-db
 :utils/set-animation-vars
 (fn [db [_ animation-vars]]
   (assoc-in db [:ui :animation-vars] animation-vars)))

(rf/reg-event-fx
 :utils/calculate-animation-vars
 [(rf/inject-cofx ::sub-inject/sub [:story-config])]
 (fn [{:keys [story-config]} _]
   (r/after-render
    (fn []
      (let [animation-vars (animation-sidebar.util/story-config-animation-vars story-config {})]
        (rf/dispatch [:utils/set-animation-vars animation-vars]))))
   {}))

(defn toggle-class! [id class]
  (fn [e]
    (.preventDefault e)
    (rf/dispatch [:ui/toggle-class! id class])))

(rf/reg-event-fx
 :download-from-url
 (fn [_ [_ url]]
   {:download-from-url url}))

(rf/reg-event-db
 :set-interval
 (fn [db [_ {:keys [path dispatch ms]}]]
   (when-some [interval-id (get-in db path)]
     (js/clearInterval interval-id))
   (let [interval-id (js/setInterval #(rf/dispatch dispatch) ms)]
     (assoc-in db path interval-id))))

(rf/reg-event-db
 :clear-interval
 (fn [db [_ path]]
   (when-some [interval-id (get-in db path)]
     (js/clearInterval interval-id)
     (rf-utils/dissoc-in db path))))

(rf/reg-event-fx
 ::debug-info
 (fn [{:keys [db]} _]
   (let [debug-data {:release       (gobj/get js/window "SENTRY_RELEASE" define/ENV-NAME)
                     :recent-events @debug/recent-event-queue
                     :db            db
                     :url           js/window.location.href}]
     {:dispatch          [:ui/alert {:message (tr [:debug-info-copied "Debug info copied to clipboard."]) :type "success"}]
      :copy-to-clipboard (with-out-str (pprint debug-data))
      :throw-exception   debug-data})))

(defn copyable? [layer]
  (not (= (:type layer) "CTA")))

(rf/reg-event-fx
 :copy-to-db-clipboard
 [persist-db-interceptor
  (rf/inject-cofx ::sub-inject/sub [::controller.sub/selection])
  (rf/inject-cofx ::sub-inject/sub [:story/current-scene])
  (rf/inject-cofx ::sub-inject/sub [:scenes/sidebar])
  (rf/inject-cofx ::sub-inject/sub [:scenes/current-layers])]
 (fn [{db             :db
       current-scene  :story/current-scene
       current-layers :scenes/current-layers
       sidebar        :scenes/sidebar
       selection      ::controller.sub/selection}
      [_ {:keys [type data]}]]
   (cond
     ;; When called with explicit type and data (from refactored COPY handler)
     (and type data)
     (let [clipboard-data {:type type
                           :data data}]
       {:db (assoc-in db [:persistent :clipboard-data] clipboard-data)
        :fx [[:dispatch [:ui/alert {:message (tr [:successfully-copied-to-clipboard "Successfully copied to clipboard."])
                                    :type    "success"}]]]})
     ;; Legacy behavior for backward compatibility (when called without explicit data)
     (and (seq selection) (= (:layout current-scene) "freestyle"))
     (let [layers         (filterv #(contains? selection (:id %)) current-layers)
           clipboard-data {:type :layers
                           :data layers}]
       (when (every? copyable? layers)
         {:db (assoc-in db [:persistent :clipboard-data] clipboard-data)}))
     (and current-scene (not sidebar))
     (let [clipboard-data {:type :scene
                           :data current-scene}]
       {:db (assoc-in db [:persistent :clipboard-data] clipboard-data)}))))

(rf/reg-event-fx
 :cut-to-db-clipboard
 [persist-db-interceptor
  (rf/inject-cofx ::sub-inject/sub [::controller.sub/selection])
  (rf/inject-cofx ::sub-inject/sub [:story/current-scene])
  (rf/inject-cofx ::sub-inject/sub [:scenes/sidebar])
  (rf/inject-cofx ::sub-inject/sub [:scenes/current-layers])]
 (fn [{db             :db
       current-scene  :story/current-scene
       current-layers :scenes/current-layers
       sidebar        :scenes/sidebar
       selection      ::controller.sub/selection}
      [_ {:keys [type data]}]]
   (cond
     ;; When called with explicit type and data (from refactored CUT handler)
     (and type data)
     (let [clipboard-data {:type type
                           :data data}]
       {:db       (assoc-in db [:persistent :clipboard-data] clipboard-data)
        :dispatch (cond
                    (= type :layers)
                    [:scenes/safe-remove-layer]
                    (= type :scene)
                    [:scenes/remove-current]
                    :else
                    nil)})
     ;; Legacy behavior for backward compatibility (when called without explicit data)
     (and (seq selection) (= (:layout current-scene) "freestyle"))
     (let [layers         (filterv #(contains? selection (:id %)) current-layers)
           clipboard-data {:type :layers
                           :data layers}]
       (when (every? copyable? layers)
         {:db       (assoc-in db [:persistent :clipboard-data] clipboard-data)
          :dispatch [:scenes/safe-remove-layer]}))
     (and current-scene (not sidebar))
     (let [clipboard-data {:type :scene
                           :data current-scene}]
       {:db       (assoc-in db [:persistent :clipboard-data] clipboard-data)
        :dispatch [:scenes/remove-current]}))))

(defn prevent-default-event
  [e event-vector]
  (.preventDefault e)
  (rf/dispatch event-vector))

(defn join-filters [filters]
  (str/join "," (filterv identity filters)))